/**
 * The Hiring Manager's dashboard -- narrower and more decision-focused than
 * the recruiter one, not a filtered copy of it. Reuses useDashboardData
 * unchanged: GET /api/jobs is already scoped server-side per role
 * (HiringManagerJobStrategy joins on the assignment relation), so a Hiring
 * Manager calling the exact same hook the recruiter dashboard uses already
 * gets only their own assigned jobs and per-job candidates -- no new
 * endpoint or query param needed.
 *
 * Two sections, in priority order:
 *   1. Candidates waiting on you -- HM_REVIEW candidates across their jobs,
 *      read as a to-do list (who, which job, a way to act), not a chart.
 *      This is their actual queue.
 *   2. The same By-job summary the recruiter dashboard shows, scoped to
 *      fewer jobs -- reused as a real component, not forked.
 */

import { Link } from 'react-router-dom';
import { Button, Spin } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import Layout from '../../../../components/layout';
import Breadcrumb from '../../../../components/common/breadcrumb';
import { User } from '../../../../fsm/users';
import { useDashboardData } from '../../hooks/useDashboardData';
import ByJobTable from '../../components/by-job-table';
import { displayCandidateName } from '../../../jobs/utils/candidateDisplay';
// Reusing By-job table's own panel/panelTitle card styling rather than a
// third copy of the same block -- both sections on this page read as the
// same kind of card.
import panelStyles from '../../components/by-job-table/index.module.css';
import styles from './index.module.css';

const breadcrumbItems = [{ title: 'Dashboard' }];

interface Props {
  user?: User;
}

interface WaitingCandidate {
  id: number;
  name?: string;
  fitScore?: number | null;
  jobId: number;
  jobTitle?: string;
}

const HiringManagerDashboard = ({ user }: Props) => {
  const data = useDashboardData();

  const waitingOnYou: WaitingCandidate[] = data.byJob.flatMap(
    ({ jobId, rows }) =>
      rows
        .filter((r) => r.status === 'HM_REVIEW')
        .map((r) => ({
          id: r.id,
          name: r.name,
          fitScore: r.fitScore,
          jobId,
          jobTitle: r.jobTitle,
        })),
  );

  return (
    <Layout user={user}>
      <Breadcrumb
        items={breadcrumbItems}
        renderRight={
          <Button
            icon={<ReloadOutlined />}
            onClick={data.reload}
            disabled={data.loading}
          >
            Refresh
          </Button>
        }
      />

      <div className={styles.wrapper}>
        <div className={styles.header}>
          <h1 className={styles.title}>Dashboard</h1>
          {data.loading && <Spin size="small" />}
        </div>

        {data.error && <p className={styles.error}>{data.error}</p>}

        <section className={panelStyles.panel}>
          <h2 className={panelStyles.panelTitle}>Candidates waiting on you</h2>
          {waitingOnYou.length === 0 ? (
            !data.loading &&
            !data.error && (
              <p className={panelStyles.empty}>
                You're caught up — nothing waiting on your review.
              </p>
            )
          ) : (
            <ul className={styles.queue}>
              {waitingOnYou.map((c) => (
                <li key={`${c.jobId}-${c.id}`} className={styles.queueRow}>
                  <div className={styles.queueInfo}>
                    <span className={styles.queueName}>
                      {displayCandidateName(c.name)}
                    </span>
                    <span className={styles.queueJob}>{c.jobTitle}</span>
                  </div>
                  <span className={styles.queueScore}>
                    {typeof c.fitScore === 'number' ? (
                      c.fitScore
                    ) : (
                      <span className={panelStyles.noScore}>&mdash;</span>
                    )}
                  </span>
                  <Link to={`/jobs/${c.jobId}/candidates/${c.id}`}>
                    <Button type="primary" size="small">
                      Review
                    </Button>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <ByJobTable jobs={data.jobs} byJob={data.byJob} />
      </div>
    </Layout>
  );
};

export default HiringManagerDashboard;
